import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useContext } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/legacy-i18n';
import { asMoney } from '@trello/legacy-i18n/formatters';
import { Button } from '@trello/nachos/button';
import type { BillingIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuote } from '../types';
import { PlanCardTotals } from './PlanCardTotals';

import styles from './StandardPlanCard.less';

const format = forTemplate('billing_page_one');

export interface StandardPlanCardProps {
  numOfMembers?: number;
  teamDisplayName: ReactNode;
  teamName: string;
  prices?: BillingQuote;
  isMonthly: boolean;
  onShowFreeTrialModal: (planName: string) => void;
  sendAnalytics: (name: string) => void;
  isInPPCohort?: boolean;
}

export const StandardPlanCard: FunctionComponent<StandardPlanCardProps> = ({
  isMonthly,
  prices,
  numOfMembers = 1,
  teamDisplayName,
  sendAnalytics,
  isInPPCohort,
}) => {
  const pricePerUser = isMonthly
    ? prices?.monthly?.subTotalPerUser
    : prices?.annual?.subTotalPerUser;

  const productCode = isMonthly
    ? prices?.monthly?.productCode
    : prices?.annual?.productCode;

  const { variation, onChooseStandard } = useContext(PlanComparisonContext);

  const onClick = useCallback(() => {
    if (variation === 'billing-page') {
      sendAnalytics('standard');
    } else if (variation === 'end-of-trial-friction-overlay') {
      Analytics.sendClickedButtonEvent({
        buttonName: 'chooseStandardButton',
        source: 'trelloPlanPricingModal',
        attributes: {
          viaEndOfTrial: true,
        },
      });
    }
    onChooseStandard?.(productCode!);
  }, [variation, onChooseStandard, productCode, sendAnalytics]);

  return (
    <section className={styles.planCard}>
      <article>
        <h3 className={styles.name}>{format('standard-name')}</h3>
        <p className={styles.info}>
          {isInPPCohort
            ? format('standard-description-personal-productivity')
            : format('standard-description-repackaging-gtm')}
        </p>
        <div className={styles.priceContainer}>
          <span className={styles.price}>
            {format('amount-usd', {
              amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
            })}
          </span>
          <span className={styles.frequency}>
            {format('per-user')}
            <br />
            {format(isMonthly ? 'per-month' : 'per-year')}
          </span>
        </div>
        <div className={styles.ctaContainer}>
          <Button
            onClick={onClick}
            testId={
              variation === 'billing-page'
                ? getTestId<BillingIds>('upgrade-team-button')
                : getTestId<BillingIds>('get-standard')
            }
          >
            {variation === 'billing-page' ||
            variation === 'reverse-trial-overlay'
              ? format('upgrade-now')
              : format('get-standard')}
          </Button>
        </div>
      </article>
      <PlanCardTotals
        isMonthly={isMonthly}
        numOfMembers={numOfMembers}
        teamName={teamDisplayName}
        prices={prices}
      />
    </section>
  );
};
